// Camera Help — public explainer page (iter-37b).
//
// A single-scroll page that walks a photographer through every common
// reason the camera might not work, and the exact fix. Written in plain
// language — no jargon, no fluff. Linked from the compatibility probe
// card and from the in-camera error overlay.
import { Link } from "react-router-dom";
import {
  CheckCircle,
  Prohibit,
  WarningCircle,
  Camera as CameraIcon,
  DeviceMobile,
  Laptop,
  Question,
  ArrowLeft,
  Lightning,
} from "@phosphor-icons/react";

export default function CameraHelpPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-8 pb-24 space-y-8" data-testid="camera-help-page">
      <div>
        <Link
          to="/setup"
          className="label-mono text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1"
          data-testid="help-back-link"
        >
          <ArrowLeft size={12} weight="bold" /> BACK TO SETUP
        </Link>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">
          CAMERA HELP
        </h1>
        <p className="text-neutral-600 mt-2">
          If your camera won't start or the app can't see it, one of the eight
          things below is almost always the reason. Each has a two-second fix.
        </p>
      </div>

      <div className="border-2 border-black bg-black text-white p-5" data-testid="help-boundary">
        <div className="label-mono text-neutral-400">THE RULE IN ONE LINE</div>
        <p className="font-mono text-sm mt-2">
          Web capture requires standard USB webcam / UVC output or a mobile device camera. For tethered DSLRs,
          run your camera maker's webcam utility or feed the HDMI output through a UVC capture card — the camera
          then shows up in the CAM 1 / CAM 2 picker like any webcam. Raw USB tethering (PTP/MTP) is native-software-only
          and no browser can read it.
        </p>
      </div>

      <SupportedList />

      <div className="space-y-6">
        <Problem
          num="01"
          title="A black screen with no error"
          who="Most common on desktops with a single webcam"
          symptom="You tapped Start, camera light doesn't come on, no popup asks for permission."
          why="Your browser is trying to open the rear camera but there isn't one — a webcam has no `facingMode`."
          fix="The app now falls back to any camera automatically. If you still see a black screen, tap RE-TEST on the Setup page. If it still fails, restart the browser tab."
          testId="problem-black-screen"
        />

        <Problem
          num="02"
          title="Camera icon in address bar is red or crossed out"
          who="Anyone who ever tapped Block on the permission popup"
          symptom="You get a 'Permission denied' error and no way to re-ask."
          why="The browser remembers your first choice. Once you tap Block, no popup will ever appear again — you must unblock it manually."
          fix={
            <>
              Look for the camera icon at the very left of the URL bar. Click it,
              choose <b>Allow</b>, then refresh the page. On iOS Safari: go to
              Settings → Safari → Camera → Allow. On Android Chrome: three-dot
              menu → Site settings → Camera → Allow.
            </>
          }
          testId="problem-permission-denied"
        />

        <Problem
          num="03"
          title="Camera fails to start on your laptop"
          who="Anyone with Zoom, Meet, Teams, OBS or Discord open"
          symptom="Error says 'camera is in use'."
          why="Only one app can hold the camera at a time. If Zoom is running in the background — even minimised — it locks the webcam."
          fix="Fully quit Zoom / Meet / Teams / OBS / Discord (not just close the window — quit the app). Then tap Retry."
          testId="problem-in-use"
        />

        <Problem
          num="04"
          title="Works on WiFi but breaks on the office network"
          who="Anyone on a corporate or school network"
          symptom="Page loads over HTTPS but camera silently fails or gives a generic error."
          why="Some corporate proxies re-write HTTPS in a way that breaks browser security checks that the camera API needs."
          fix="Try on your phone's data connection or a home network to confirm. If it works there, the issue is your office proxy — ask IT to allow aiimagesuites.pro through the SSL-inspection allowlist."
          testId="problem-corp-network"
        />

        <Problem
          num="05"
          title="Only the front camera works on your phone"
          who="Phones with an old browser or an unusual custom launcher"
          symptom="Selfie camera opens instead of the rear one."
          why="Some browsers ignore the 'rear camera' hint. Common on old Samsung Internet, in-app browsers (Facebook, Instagram, LinkedIn's in-app viewer), and certain manufacturer skins."
          fix="Copy the app URL and open it in the real Chrome or Safari — not the in-app browser. If you're on the real browser and still stuck, use the CAM 1 / CAM 2 picker in the top bar of the capture screen to pick the rear lens manually."
          testId="problem-front-only"
        />

        <Problem
          num="06"
          title="Your DSLR / mirrorless camera won't show up"
          who="Anyone plugging a Canon, Sony, Nikon, Fuji, etc. into a laptop"
          symptom="The camera picker only shows your webcam, not your real camera."
          why={
            <>
              This is a <b>hard browser limit</b>, not a bug. Professional
              cameras connect as storage devices (MTP/PTP), not as webcams. No
              website on Earth can capture from them without a native helper app
              installed on your computer.
            </>
          }
          fix={
            <>
              Three options:
              <ol className="list-decimal list-inside mt-2 space-y-1">
                <li>Install your camera maker's webcam-mode app (Canon EOS Webcam Utility, Sony Imaging Edge Webcam, Nikon Webcam Utility, Fujifilm X Webcam) or use an HDMI capture card. Either makes the camera appear as a normal webcam, which this site <b>can</b> use — pick it in the CAM 1 / CAM 2 picker.</li>
                <li>Use your phone as the camera — modern phone lenses are excellent for e-commerce work.</li>
                <li>Shoot to the SD card as usual, pop it into your computer, then use the standard Sync flow on <span className="font-mono">aiimagesuite.pro</span> to upload the files.</li>
              </ol>
              Plugging the camera straight in over USB alone will never work in any browser.
            </>
          }
          testId="problem-dslr"
        />

        <Problem
          num="07"
          title="Photos look grainy or low-resolution"
          who="Anyone on a low-end phone or old webcam"
          symptom="Captures look worse than your phone's normal camera app."
          why="The app asks for 1920×1440 but your device gave whatever it had. Native camera apps often use higher-res sensors than the WebRTC interface exposes."
          fix="Run TEST NOW on the Setup page — the report shows exactly what resolution your device gives us. If it says 640×480 or lower, that's the hardware ceiling for web access. Modern phones (2019+) usually give 1920×1080 or better."
          testId="problem-low-res"
        />

        <Problem
          num="08"
          title="The page won't open on http://"
          who="Anyone testing on a local network without HTTPS"
          symptom="Camera never starts, no error."
          why="Browsers block camera access on plain http:// — it's a security rule, not our choice. Only https:// (or localhost) allowed."
          fix={
            <>
              Always visit <span className="font-mono">https://aiimagesuites.pro</span> or your
              preview URL — never the raw IP address or an http:// link.
            </>
          }
          testId="problem-http"
        />
      </div>

      <div className="border-2 border-black p-5 bg-neutral-50" data-testid="help-still-stuck">
        <div className="flex items-center gap-2">
          <Question size={20} weight="bold" />
          <div className="font-display font-bold tracking-tighter text-lg">STILL STUCK?</div>
        </div>
        <p className="text-sm mt-2">
          Tap <b>TEST NOW</b> on the Setup page. The report tells you exactly
          which of the eight problems above applies to your device — usually in
          under two seconds.
        </p>
        <Link
          to="/setup"
          className="btn-flat mt-3 inline-flex items-center gap-1"
          data-testid="help-cta-test"
        >
          <CameraIcon size={16} weight="bold" /> RUN THE TEST
        </Link>
      </div>
    </div>
  );
}

function SupportedList() {
  const works = [
    { icon: DeviceMobile, label: "iPhone (iOS 14+ Safari)" },
    { icon: DeviceMobile, label: "Android phones (Chrome, Samsung Internet, Firefox)" },
    { icon: Laptop, label: "Laptop / desktop with built-in webcam" },
    { icon: Laptop, label: "USB webcams — any standard UVC device (Logitech, Anker, etc.)" },
    { icon: CameraIcon, label: "Continuity Camera (iPhone as Mac webcam)" },
    { icon: CameraIcon, label: "DSLR / mirrorless running the maker's webcam utility (EOS Webcam Utility, Imaging Edge Webcam…)" },
    { icon: CameraIcon, label: "DSLR / mirrorless via an HDMI → UVC capture card (Elgato Cam Link, etc.)" },
  ];
  const fails = [
    { icon: CameraIcon, label: "DSLR / mirrorless tethered over raw USB (PTP/MTP) with no virtual-webcam driver — browsers cannot read that stream" },
    { icon: DeviceMobile, label: "In-app browsers (Instagram, Facebook, LinkedIn) — open the real Chrome / Safari instead" },
    { icon: Laptop, label: "Very old browsers (IE, Safari <11, Chrome <53)" },
  ];
  const Row = ({ r, ok, i }) => (
    <li className={`flex items-start gap-2 ${ok ? "" : "text-neutral-600"}`} data-testid={`help-${ok ? "works" : "fails"}-${i}`}>
      {ok ? <CheckCircle size={14} weight="bold" className="text-green-700 mt-0.5 shrink-0" /> : <Prohibit size={14} weight="bold" className="text-red-600 mt-0.5 shrink-0" />}
      <r.icon size={14} weight="bold" className="mt-0.5 shrink-0" />
      <span>{r.label}</span>
    </li>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="help-supported-list">
      <div className="border-2 border-black p-5">
        <div className="flex items-center gap-2">
          <Lightning size={18} weight="bold" />
          <div className="font-display font-bold tracking-tighter text-lg">WHAT WORKS</div>
        </div>
        <ul className="mt-3 space-y-1 text-sm font-mono">
          {works.map((r, i) => <Row key={i} r={r} ok i={i} />)}
        </ul>
      </div>
      <div className="border-2 border-black p-5 bg-[#F8F9FA]">
        <div className="flex items-center gap-2">
          <Prohibit size={18} weight="bold" className="text-red-600" />
          <div className="font-display font-bold tracking-tighter text-lg">WHAT DOESN'T</div>
        </div>
        <ul className="mt-3 space-y-1 text-sm font-mono">
          {fails.map((r, i) => <Row key={i} r={r} ok={false} i={i} />)}
        </ul>
      </div>
    </div>
  );
}

function Problem({ num, title, who, symptom, why, fix, testId }) {
  return (
    <div className="border-2 border-black p-5 space-y-2" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="font-display font-black text-2xl leading-none tracking-tighter shrink-0">
          {num}
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg leading-tight">{title}</div>
          <div className="label-mono text-[10px] text-neutral-500 mt-0.5">{who}</div>
        </div>
        <WarningCircle size={18} weight="bold" className="text-neutral-500 shrink-0" />
      </div>
      <div className="text-sm space-y-2 pt-2 border-t border-neutral-200">
        <div>
          <span className="label-mono text-[10px] text-neutral-500">SYMPTOM</span>
          <div>{symptom}</div>
        </div>
        <div>
          <span className="label-mono text-[10px] text-neutral-500">WHY</span>
          <div>{why}</div>
        </div>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 pl-3 py-2">
          <span className="label-mono text-[10px] text-yellow-800">FIX</span>
          <div>{fix}</div>
        </div>
      </div>
    </div>
  );
}
